import { Alert, Avatar, Button, Modal, Card, Col, Flex, Row, Typography, Empty, notification, Spin, message, Input, Select, Table, Drawer, Tag, Space, Switch, Tabs } from 'antd';
import React, { useEffect, useState, useMemo } from 'react';
import { User } from '@models/user';
import { Artwork } from '../../../data/models/artwork';
import { DeleteOutlined, EditOutlined, LoadingOutlined, MoreOutlined, UserOutlined, EyeOutlined, SearchOutlined } from '@ant-design/icons';
import { makeRequest } from '@helpers/axios-manipulator';
import * as S from './styles';
import { getCurrentUtcDate } from '../../helpers/current-utc-date-generator';
import { useSelector } from 'react-redux';
import { RootState } from '../../../redux/appState';
import { USER_TYPES } from '../../../constants';

const { Text, Title } = Typography;

const DealersScreen = () => {
  const [dealers, setDealers] = useState<any[]>([]);
  const [loadingDealers, setLoadingDealers] = useState(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [selectedDealer, setSelectedDealer] = useState<any>(null);
  
  const [loading, setLoading] = useState(false);
  const [artworks, setArtworks] = useState<Artwork[]>([]);
  const [filteredArtworks, setFilteredArtworks] = useState<Artwork[]>([]);
  const [selectedArtworks, setSelectedArtworks] = useState<Artwork[]>([]);
  const [filterType, setFilterType] = useState<string | undefined>();
  const [allArtists, setAllArtists] = useState<any[]>([]);
  
  const [editContractModal, setEditContractModal] = useState(false);
  const [unlinkContractModal, setUnlinkContractModal] = useState(false);
  const [selectedEditArtwork, setSelectedEditArtwork] = useState<Artwork>({} as Artwork);
  
  const [api, contextHolder] = notification.useNotification();
  const [messageApi, messageContextHolder] = message.useMessage();
  const user = useSelector((state: RootState) => state.user.user);

  const [showAssignedOnly, setShowAssignedOnly] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [tableSearchValue, setTableSearchValue] = useState('');

  const assignedArtists = useMemo(() => {
    const artistMap = new Map();
    artworks.filter(art => art.contract_data.hasRepRegistry).forEach(art => {
      const artistId = art.created_by_artist_id;
      if (!artistMap.has(artistId)) {
        artistMap.set(artistId, {
          id: artistId,
          name: art.artist_display_name || 'Unknown Artist',
          username: art.artist_username || 'unknown',
          profile_image_url: art.artist_profile_image_url,
          pieces: 0
        });
      }
      artistMap.get(artistId).pieces += 1;
    });
    return Array.from(artistMap.values());
  }, [artworks]);

  const artistFilterOptions = useMemo(() => {
    const formatOption = (a: any) => ({
      value: a.id,
      label: (
        <Space>
          <Avatar size="small" src={a.profile_image_url || undefined} icon={<UserOutlined />} />
          <span style={{ fontWeight: 500 }}>{a.display_name || a.username}</span>
          <span style={{ color: '#8c8c8c', fontSize: '12px' }}>@{a.username}</span>
        </Space>
      ),
      username: a.username,
      displayName: a.display_name
    });

    if (!showAssignedOnly) {
      return allArtists.map(formatOption);
    }
    
    const assignedArtistIds = new Set(
      artworks
        .filter(art => art.contract_data.hasRepRegistry)
        .map(art => art.created_by_artist_id)
    );
    
    return allArtists
      .filter(a => assignedArtistIds.has(a.id))
      .map(formatOption);
  }, [allArtists, artworks, showAssignedOnly]);


  const fetchDealers = async () => {
    setLoadingDealers(true);
    try {
      const response = await makeRequest({
        origin: window.location.origin,
        method: 'get',
        url: '/api-dealers-stats',
      });
      setDealers(response);
    } catch (error) {
      console.error('Error fetching dealer stats:', error);
      const errorMsg = error.response?.data?.details || 'Failed to fetch dealers';
      messageApi.error(errorMsg);
    } finally {
      setLoadingDealers(false);
    }
  };

  const fetchAvailableArtworks = async (dealerId: string) => {
    setLoading(true);
    try {
      const response = await makeRequest({
        origin: window.location.origin,
        method: 'get',
        url: `/api-inventory-management-data?repId=${dealerId}`,
      });
      
      const dealerArtworksResponse = await makeRequest({
        origin: window.location.origin,
        method: 'get',
        url: `/api-dealer-artworks/${dealerId}?page=1&limit=500`,
      });

      let availableArtworks = response.inventory || [];
      const assignedArtworks = dealerArtworksResponse.artworks || [];

      const mergedArtworks = availableArtworks.map((art: any) => {
        const assigned = assignedArtworks.find((a: any) => a.id === art.id);
        return {
          ...art,
          ...(assigned || {}), // Merge all fields from assigned (including artist_username, artist_display_name, etc.)
          contract_data: assigned ? assigned.contract_data : { hasRepRegistry: false, status: 'none', fee: art.commission || 0 }
        };
      });

      setArtworks(mergedArtworks);
      
      const artists = response.artists?.rows || response.artists || [];
      setAllArtists(artists);

    } catch (error) {
      console.error('Error fetching available artworks:', error);
      messageApi.error('Failed to fetch artworks');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDealers();
  }, []);

  useEffect(() => {
    let results = [...artworks];
    
    if (showAssignedOnly) {
      results = results.filter(art => art.contract_data.hasRepRegistry);
    }
    
    if (filterType === 'artist' && searchValue) {
      results = results.filter(art => art.created_by_artist_id === searchValue);
    } else if (filterType === 'title' && searchValue) {
      results = results.filter(art => art.title?.toLowerCase().includes(searchValue.toLowerCase()));
    } else if (filterType === 'placard' && searchValue) {
      results = results.filter(art => art.placard_id?.toLowerCase().includes(searchValue.toLowerCase()));
    }
    
    setFilteredArtworks(results);
  }, [artworks, filterType, searchValue, showAssignedOnly]);

  const openDealerManagement = (dealer: any) => {
    setSelectedDealer(dealer);
    setIsDrawerOpen(true);
    fetchAvailableArtworks(dealer.id);
  };

  const toggleArtwork = (art: Artwork) => {
    setSelectedArtworks((prev) => 
      prev.some(item => item.id === art.id)
        ? prev.filter(item => item.id !== art.id)
        : [...prev, art]
    );
  };

  const generateNewContracts = async () => {
    if (!selectedDealer) return;
    const contracts = selectedArtworks.map((art) => ({
      artist_id: art.created_by_artist_id,
      artwork_id: art.id,
      rep_id: selectedDealer.id,
      rep_type: 4,
      status: 'active',
      commission: art.contract_data.fee,
      creation_date: getCurrentUtcDate(),
      last_updated_date: getCurrentUtcDate(),
      last_updated_by: user.id
    }));
    
    setLoading(true);
    try {
      await makeRequest({
        origin: window.location.origin,
        method: 'post',
        url: '/api-manipulate-artwork-contracts',
        data: { contracts },
      });
      messageApi.success('Contracts created successfully!');
      fetchAvailableArtworks(selectedDealer.id);
      setSelectedArtworks([]);
      fetchDealers();
    } catch (error) {
      messageApi.error('Failed to create contracts');
    } finally {
      setLoading(false);
    }
  };

  const unlinkContract = async () => {
    if (!selectedDealer) return;
    setLoading(true);
    try {
      await makeRequest({
        origin: window.location.origin,
        method: 'put',
        url: `/api-update-art-x-rep`,
        data: {
          contract: {
            status: 'inactive',
            commission: selectedEditArtwork.contract_data.fee,
            last_updated_Date: getCurrentUtcDate(),
            last_updated_by: user.id,
            artwork_id: selectedEditArtwork.id
          }
        },
      });
      messageApi.success('Artwork unlinked successfully!');
      fetchAvailableArtworks(selectedDealer.id);
      setUnlinkContractModal(false);
      fetchDealers();
    } catch (error) {
      messageApi.error('Failed to unlink artwork');
    } finally {
      setLoading(false);
    }
  };

  const saveEditedContract = async () => {
    if (!selectedDealer) return;
    setLoading(true);
    try {
      await makeRequest({
        origin: window.location.origin,
        method: 'put',
        url: `/api-update-art-x-rep`,
        data: {
          contract: {
            status: selectedEditArtwork.contract_data.status,
            commission: selectedEditArtwork.contract_data.fee,
            last_updated_Date: getCurrentUtcDate(),
            last_updated_by: user.id,
            artwork_id: selectedEditArtwork.id
          }
        },
      });
      messageApi.success('Contract updated successfully!');
      fetchAvailableArtworks(selectedDealer.id);
      setEditContractModal(false);
    } catch (error) {
      messageApi.error('Failed to update contract');
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    {
      title: 'Profile',
      dataIndex: 'profile_image_url',
      key: 'profile',
      render: (url: string) => <Avatar src={url} icon={<UserOutlined />} />,
    },
    {
      title: 'Username',
      dataIndex: 'username',
      key: 'username',
      sorter: (a: any, b: any) => a.username.localeCompare(b.username),
    },
    {
      title: 'Account ID',
      dataIndex: 'id',
      key: 'id',
      ellipsis: true,
      render: (id: string) => <Text copyable>{id}</Text>,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => (
        <Tag color={status === 'Y' ? 'green' : 'red'}>
          {status === 'Y' ? 'Active' : 'Inactive'}
        </Tag>
      ),
    },
    {
      title: '# Artists Rep',
      dataIndex: 'artists_count',
      key: 'artists_count',
      sorter: (a: any, b: any) => a.artists_count - b.artists_count,
    },
    {
      title: '# Artworks Rep',
      dataIndex: 'artworks_count',
      key: 'artworks_count',
      sorter: (a: any, b: any) => a.artworks_count - b.artworks_count,
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: any, record: any) => (
        <Button 
          type="primary" 
          icon={<EyeOutlined />} 
          onClick={() => openDealerManagement(record)}
        >
          Manage
        </Button>
      ),
    },
  ];

  return (
    <S.DealersContainer>
      {contextHolder}
      {messageContextHolder}
      <Title level={2}>Dealers & Representatives</Title>
      
      <div style={{ marginBottom: 16 }}>
        <Input
          placeholder="Search by username..."
          prefix={<SearchOutlined style={{ color: 'rgba(0,0,0,.25)' }} />}
          onChange={e => setTableSearchValue(e.target.value)}
          style={{ width: 300 }}
          allowClear
        />
      </div>

      <Table 
        dataSource={(dealers || []).filter(d => 
          d.username?.toLowerCase().includes(tableSearchValue.toLowerCase())
        )} 
        columns={columns} 
        loading={loadingDealers}
        rowKey="id"
        pagination={{ pageSize: 10 }}
      />

      <Drawer
        title={`Managing: ${selectedDealer?.username || 'Dealer'}`}
        placement="right"
        size="large"
        onClose={() => setIsDrawerOpen(false)}
        open={isDrawerOpen}
        extra={
          <Space>
            {selectedArtworks.length > 0 && (
              <Button type="primary" onClick={generateNewContracts}>
                Assign {selectedArtworks.length} Piece{selectedArtworks.length > 1 ? 's' : ''}
              </Button>
            )}
          </Space>
        }
      >
        {loading ? (
          <Flex align="center" justify="center" style={{ height: '100%' }}>
            <Spin size="large" />
          </Flex>
        ) : (
          <Tabs defaultActiveKey="artists" items={[
            {
              key: 'artists',
              label: 'Artists Rep',
              children: (
                <Table 
                  dataSource={assignedArtists}
                  pagination={false}
                  rowKey="id"
                  columns={[
                    {
                      title: 'Profile',
                      dataIndex: 'profile_image_url',
                      key: 'profile',
                      render: (url: string) => <Avatar src={url} icon={<UserOutlined />} />,
                    },
                    {
                      title: 'Artist Name',
                      dataIndex: 'name',
                      key: 'name',
                    },
                    {
                      title: 'Username',
                      dataIndex: 'username',
                      key: 'username',
                      render: (username: string) => <Text type="secondary">@{username}</Text>,
                    },
                    {
                      title: '# Pieces',
                      dataIndex: 'pieces',
                      key: 'pieces',
                      render: (count: number) => <Tag color="blue">{count} Assigned</Tag>
                    }
                  ]}
                />
              )
            },
            {
              key: 'inventory',
              label: 'Inventory Management',
              children: (
                <>
                  <div className='mb-4'>
                    <Flex justify="space-between" align="center" className="mb-2">
                      <Title level={5} style={{ margin: 0 }}>Inventory Filters</Title>
                      <Space>
                        <Text type="secondary">Assigned Only</Text>
                        <Switch 
                          size="small" 
                          checked={showAssignedOnly} 
                          onChange={setShowAssignedOnly} 
                        />
                      </Space>
                    </Flex>
                    <Row gutter={[8, 8]}>
                      <Col span={8}>
                        <Select
                          placeholder="Search by"
                          style={{ width: '100%' }}
                          value={filterType}
                          onChange={v => { setFilterType(v); setSearchValue(''); }}
                          options={[
                            { value: 'artist', label: 'Artist' },
                            { value: 'title', label: 'Artwork Title' },
                            { value: 'placard', label: 'Placard ID' },
                          ]}
                        />
                      </Col>
                      <Col span={16}>
                        {filterType === 'artist' && (
                          <Select
                            showSearch
                            placeholder="Select Artist"
                            style={{ width: '100%' }}
                            options={artistFilterOptions}
                            onChange={setSearchValue}
                            filterOption={(input, option) => String(option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
                          />
                        )}
                        {filterType === 'title' && (
                          <Input 
                            placeholder="Enter title..." 
                            onChange={e => setSearchValue(e.target.value)} 
                          />
                        )}
                        {filterType === 'placard' && (
                          <Input 
                            placeholder="Enter placard ID..." 
                            onChange={e => setSearchValue(e.target.value)} 
                          />
                        )}
                      </Col>
                    </Row>
                  </div>

                  <Row gutter={[12, 12]}>
                    {filteredArtworks.map(art => (
                      <Col span={8} key={art.id}>
                        <Card
                          hoverable={!art.contract_data.hasRepRegistry}
                          variant="outlined"
                          className={selectedArtworks.some(a => a.id === art.id) ? 'selected-card' : ''}
                          onClick={() => !art.contract_data.hasRepRegistry && toggleArtwork(art)}
                          styles={{ body: { padding: '8px' } }}
                          cover={
                            <div style={{ position: 'relative' }}>
                              <img 
                                alt={art.title} 
                                src={art.image_url} 
                                style={{ height: 100, width: '100%', objectFit: 'cover' }} 
                              />
                              {selectedArtworks.some(a => a.id === art.id) && (
                                <div style={{ 
                                  position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, 
                                  background: 'rgba(56, 158, 13, 0.6)', display: 'flex', 
                                  alignItems: 'center', justifyContent: 'center' 
                                }}>
                                  <Tag color="success" style={{ margin: 'auto' }}>Selected</Tag>
                                </div>
                              )}
                            </div>
                          }
                          actions={art.contract_data.hasRepRegistry ? [
                            <EditOutlined key="edit" size={12} onClick={(e) => {
                              e.stopPropagation();
                              setSelectedEditArtwork(art);
                              setEditContractModal(true);
                            }} />,
                            <DeleteOutlined key="delete" size={12} onClick={(e) => {
                              e.stopPropagation();
                              setSelectedEditArtwork(art);
                              setUnlinkContractModal(true);
                            }} />,
                          ] : []}
                        >
                          <Card.Meta 
                            title={<Text strong style={{ fontSize: '12px' }} ellipsis>{art.title}</Text>} 
                            description={
                              <Space direction="vertical" size={0}>
                                <Text type="secondary" style={{ fontSize: '11px' }} ellipsis>{art.artist_display_name}</Text>
                                {art.contract_data.hasRepRegistry ? (
                                  <Tag color="blue" style={{ fontSize: '10px', margin: 0 }}>{art.contract_data.fee}%</Tag>
                                ) : (
                                  <Tag color="default" style={{ fontSize: '10px', margin: 0 }}>Unlinked</Tag>
                                )}
                              </Space>
                            }
                          />
                        </Card>
                      </Col>
                    ))}
                  </Row>
                </>
              )
            }
          ]} />
        )}
      </Drawer>

      {/* Modals */}
      <Modal
        title="Edit Contract"
        open={editContractModal}
        onOk={saveEditedContract}
        onCancel={() => setEditContractModal(false)}
      >
        <Text>Commission Percentage (%)</Text>
        <Input 
          type="number"
          value={selectedEditArtwork?.contract_data?.fee} 
          onChange={e => setSelectedEditArtwork({
            ...selectedEditArtwork, 
            contract_data: { ...selectedEditArtwork.contract_data, fee: parseInt(e.target.value) }
          })}
        />
      </Modal>

      <Modal
        title="Unlink Artwork"
        open={unlinkContractModal}
        onOk={unlinkContract}
        onCancel={() => setUnlinkContractModal(false)}
        okText="Unlink"
        okType="danger"
      >
        <p>Are you sure you want to unlink <b>{selectedDealer?.username}</b> from <b>{selectedEditArtwork.title}</b>?</p>
      </Modal>
    </S.DealersContainer>
  );
};

export default DealersScreen;